import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  useTheme,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  NotificationsActive as AppIcon,
  Http as WebhookIcon,
  Work as RoleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Contact } from '../../types/contact';

interface ContactDetailProps {
  open: boolean;
  onClose: () => void;
  contact: Contact | null;
}

const ContactDetail: React.FC<ContactDetailProps> = ({ open, onClose, contact }) => {
  const theme = useTheme();

  if (!contact) {
    return null;
  }

  // 연락 방법에 따른 아이콘과 텍스트
  const getContactTypeInfo = (type: Contact['type']) => {
    switch (type) {
      case 'email':
        return { icon: <EmailIcon />, label: '이메일' };
      case 'sms':
        return { icon: <PhoneIcon />, label: 'SMS' };
      case 'app':
        return { icon: <AppIcon />, label: '앱 알림' };
      case 'webhook':
        return { icon: <WebhookIcon />, label: '웹훅' };
      default:
        return { icon: <PersonIcon />, label: type };
    }
  };

  // 날짜 형식 변환
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // 사용자 이니셜로 아바타 생성
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const contactTypeInfo = getContactTypeInfo(contact.type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 56,
              height: 56,
            }}
          >
            {getInitials(contact.name)}
          </Avatar>
          <Box>
            <Typography variant="h6">{contact.name}</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {contact.role}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          담당자 정보
        </Typography>
        
        <List disablePadding>
          <ListItem>
            <ListItemIcon>{contactTypeInfo.icon}</ListItemIcon>
            <ListItemText
              primary={contactTypeInfo.label}
              secondary={contact.target}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon><RoleIcon /></ListItemIcon>
            <ListItemText 
              primary="역할" 
              secondary={contact.role}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              {contact.is_active ? <ActiveIcon color="success" /> : <InactiveIcon color="error" />}
            </ListItemIcon>
            <ListItemText 
              primary="상태" 
              secondary={
                <Chip
                  label={contact.is_active ? '활성' : '비활성'}
                  color={contact.is_active ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              }
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon><CalendarIcon /></ListItemIcon>
            <ListItemText 
              primary="등록일" 
              secondary={formatDate(contact.created_at)}
            />
          </ListItem>
          
          {contact.updated_at && (
            <ListItem>
              <ListItemIcon><CalendarIcon /></ListItemIcon>
              <ListItemText 
                primary="최종 수정일" 
                secondary={formatDate(contact.updated_at)}
              />
            </ListItem>
          )}
        </List>
        
        {contact.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              설명
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {contact.description}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDetail; 